import { useEffect, useRef, useCallback } from 'react';
const FOCUSABLE_SELECTORS = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'details > summary',
].join(', ');
/**
 * Traps keyboard focus within a container element while active.
 * Supports Tab and Shift+Tab cycling, and optionally restores
 * focus to the previously active element on deactivation.
 */
export function useFocusTrap({ active, returnFocus = true, }) {
    const containerRef = useRef(null);
    const previousFocusRef = useRef(null);
    const getFocusableElements = useCallback(() => {
        if (!containerRef.current)
            return [];
        // `querySelectorAll` with a selector list is specified to return matches
        // in document tree order, but recent jsdom/nwsapi regressions have
        // returned matches grouped by selector instead. Sort explicitly by
        // document position so Tab cycling stays stable regardless of the
        // underlying selector engine.
        return Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTORS))
            .filter((el) => !el.closest('[aria-hidden="true"]'))
            .sort((a, b) => {
            if (a === b)
                return 0;
            const position = a.compareDocumentPosition(b);
            if (position & Node.DOCUMENT_POSITION_FOLLOWING)
                return -1;
            if (position & Node.DOCUMENT_POSITION_PRECEDING)
                return 1;
            return 0;
        });
    }, []);
    const focusFirst = useCallback(() => {
        const elements = getFocusableElements();
        if (elements.length > 0) {
            elements[0].focus();
        }
        else {
            containerRef.current?.focus();
        }
    }, [getFocusableElements]);
    // Store previous focus and set initial focus when activated
    useEffect(() => {
        if (active) {
            previousFocusRef.current = document.activeElement;
            // Defer to let the DOM settle (e.g. after animation frame)
            const rafId = requestAnimationFrame(() => {
                focusFirst();
            });
            return () => cancelAnimationFrame(rafId);
        }
        else if (returnFocus && previousFocusRef.current) {
            previousFocusRef.current.focus();
            previousFocusRef.current = null;
        }
        return undefined;
    }, [active, returnFocus, focusFirst]);
    // Handle Tab / Shift+Tab cycling
    useEffect(() => {
        if (!active)
            return;
        const handleKeyDown = (event) => {
            if (event.key !== 'Tab')
                return;
            const elements = getFocusableElements();
            if (elements.length === 0) {
                event.preventDefault();
                return;
            }
            const firstEl = elements[0];
            const lastEl = elements[elements.length - 1];
            const activeEl = document.activeElement;
            if (event.shiftKey) {
                if (activeEl === firstEl || !containerRef.current?.contains(activeEl)) {
                    event.preventDefault();
                    lastEl.focus();
                }
            }
            else {
                if (activeEl === lastEl || !containerRef.current?.contains(activeEl)) {
                    event.preventDefault();
                    firstEl.focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [active, getFocusableElements]);
    return { containerRef, focusFirst };
}
